import { EmployeeNameRM, EmployeeProfilePatchRequest, FeatureFlagOption, OnboardingApplicationResponse, OnboardingDivisionResponse, PagedEntityList } from '@herbjoy/apex-api-models'
import angular, { IQService } from 'angular'
import moment from 'moment'
import { FeatureManager } from '../../../../core/feature-flags/feature-manager.service'
import { EmployeeService, OnboardingApplicationService, OnboardingStatusService } from '../../../../core/services'
import { UtilityService } from '../../../../core/services/utility.service'
import { EditSgiIdAssignmentModal } from '../../edit-sgi-id-assignment/edit-sgi-id-assignment-modal.component'
import { AddApplicantToRosterModal } from '../add-to-roster/add-applicant-to-roster-modal.component'
import { AssignOnboardingEmployeeModal } from '../assign-onboarding-employee/assign-onboarding-employee-modal.component'
import { EditOnboardingApplicationStatusModal } from '../edit-onboarding-application-status/edit-onboarding-application-status-modal.component'
import { EditOnboardingApplicationModal } from '../edit-onboarding-application/edit-onboarding-application-modal.component'
import { OnboardingApplicationDetailModal } from '../onboarding-application-detail/onboarding-application-detail-modal.component'
import { TransferOnboardingApplicationModal } from '../transfer-onboarding-application/transfer-onboarding-application.component'
import templateUrl from './onboarding-applications-list.component.html'

export interface OnboardingApplication {
    OnboardingApplicationID: number;
    DivisionID: number;
    IcdOfficeID: number;
    EmployeeID: number;
    Applicant: {
        FirstName: string;
        LastName: string;
        EmailAddress: string;
        CellPhone: {
            PhoneNumber: string;
            Extension: string;
        };
        DateOfBirth: Date;
    };
    ReferenceFields: any;
    StatusHistory: any;
}
export const OnboardingApplicationsListComponent = {
    templateUrl,
    controllerAs: "vm",
    controller: class OnboardingApplicationsListController {
        addedEmployee: any
        patchRequest: EmployeeProfilePatchRequest
        saveEmployeementRecord: (employee: any) => void

        constructor(private $uibModal, private $q: IQService, private apexApiUrl: string, private divisionService,
            private onboardingDivisionService, private employeeService: EmployeeService,
            private onboardingApplicationService: OnboardingApplicationService,
            private onboardingRosterService, private onboardingStatusService: OnboardingStatusService,
            private featureManager: FeatureManager,
            private utilityService: UtilityService) {
            'ngInject'
        }

        onboardingStatuses: any;
        referenceFields: any;
        isAdminView: boolean;
        showRosterPasswordEditor: boolean;
        showOnboardingPasswordEditor: boolean;
        exportXlsxUrl: string;
        loaded = false;
        filters: any = {};
        pageNumber = 1;
        pageSize = 50;
        roleAccess: any;
        sortColumn: any = { field: "FirstName", dir: -1 };
        sortClass = (dir: number) => dir > 0 ? "glyphicon fas fa-sort-amount-down-alt" : "glyphicon fas fa-sort-amount-down";
        close: any;
        afterSave: any;
        onboardingApplication: OnboardingApplication;
        divisions: OnboardingDivisionResponse;
        onboardingApplications: PagedEntityList<OnboardingApplicationResponse>;

        $onInit() {
            this.activate();
            this.utilityService.debounce(this.onChangeFilters, 100);
            this.exportXlsxUrl = this.apexApiUrl + "onboarding-applications.xlsx";
        }

        activate() {

            this.utilityService.showLoading();
            const access = this.employeeService.retrievePermissions({
                stateName: "onboarding.application.list"
            });
            const identity = this.employeeService.retrieveIdentity();
            const roleAccessLoaded = this.$q.all([access, identity])
                .then(([access, identity]) => {
                    this.roleAccess = {
                        IsSiteAdmin: identity.IsSiteAdmin,
                        CanUpdate: access.CanUpdate,
                        CanCreate: access.CanCreate
                    }
                    this.isAdminView = identity.IsSiteAdmin
                });
            const featuresLoaded = this.featureManager.ensureLoaded()
                .then(() => this.featureManager.getFeatureFlags())
                .then(prefs => {
                    //TODO: Should be implemented as permissions.
                    this.showRosterPasswordEditor =
                        prefs["security.allowSettingPassword"] === FeatureFlagOption.Enabled;
                    this.showOnboardingPasswordEditor =
                        prefs["security.allowSettingPasswordWhileOnboarding"] === FeatureFlagOption.Enabled;
                });
            const loadInitialData = this.onboardingDivisionService.retrieveOnboardingDivisions()
                .then(divisions => {
                    this.divisions = divisions;
                    //TODO: Make generic again.
                    this.filters.division = divisions.filter(division => {
                        return division.DivisionID === this.divisionService.divisionIDs.Rogers;
                    })[0];
                    this.onboardingStatuses = this.filters.division ?
                        this.filters.division.OnboardingPolicy.ApplicationStatuses
                            .filter(p => !p.Inactive) : [];
                    this.filters = this.loadFilters();
                    return this.loadOnboardingApplications();
                });

            this.$q.all([loadInitialData, roleAccessLoaded, featuresLoaded])
                .then(() => {
                    this.loaded = true;
                })
                .catch(ex => {
                    console.error(ex);
                    alert("Initial load failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    this.utilityService.hideLoading();
                });
        }

        loadOnboardingApplications() {
            const param = this.generateQueryParameter();
            return this.onboardingApplicationService.retrieveOnboardingApplicationsPage(param.PageIndex, param)
                .then(applications => {
                    this.onboardingApplications = applications;
                    return this.onboardingApplications;
                })
                .then(applications => {
                    this.referenceFields = this.unpivotReferenceFields(applications.Entities);

                    return applications;
                });
        }

        unpivotReferenceFields(applications) {
            let referenceFields = { names: {}, list: [] };

            referenceFields = applications.reduce((referenceFields, application) => {
                application.ReferenceFields.forEach(field => {
                    if (field.ShowInGrid && !referenceFields.names[field.Name]) {
                        const fieldPolicy = {
                            Name: field.Name,
                            ValueType: field.ValueType
                        };
                        referenceFields.list.push(fieldPolicy);
                        referenceFields.names[field.Name] = true;
                    }
                });
                return referenceFields;
            }, referenceFields);

            if (this.referenceFields) {
                // Never explicitly remove reference fields that have already been loaded; just
                // try to re-compile them in the same order.
                referenceFields = this.referenceFields.reduce((referenceFields, existing) => {
                    if (!referenceFields.names[existing.Name]) {
                        referenceFields.names[existing.Name] = true;
                        referenceFields.list.push(existing);
                    }
                    return referenceFields;
                }, referenceFields)
            }

            // Add on any reference fields that are being used to run filters, but aren't in the list.
            // Otherwise, removing these filters when there are no results is impossible.
            if (this.filters.ReferenceFields) {
                referenceFields = Object.keys(this.filters.ReferenceFields).reduce((referenceFields, key) => {
                    if (referenceFields.names[key]) {
                        return referenceFields;
                    }

                    referenceFields.names[key] = true;
                    referenceFields.list.push({
                        Name: key,
                        ValueType: "String"
                    });
                    return referenceFields;
                }, referenceFields);
            }

            return referenceFields.list;
        }

        generateQueryParameter() {
            const referenceFields = this.filters.ReferenceFields ?
                Object.keys(this.filters.ReferenceFields).reduce((fields, key) => {
                    // Map to key-value pair object
                    const value = this.filters.ReferenceFields[key]
                        .replace(/~=/, "")
                        .trim();
                    if (value) {
                        fields.push(key + "~=" + value);
                    }
                    return fields;
                }, []) : [];
            const params = {
                PageIndex: this.filters.PageNumber - 1,
                PageSize: this.filters.PageSize,
                FirstName: this.filters.FirstName,
                LastName: this.filters.LastName,
                StatusName: this.filters.StatusName,
                ReferenceFields: referenceFields,
                OwnerName: this.filters.OwnerName,
                SubmittedBy: this.filters.SubmittedBy,
                SortFields: this.filters.SortFields,
                SubmittedAfter: this.filters.createdOnStart,
                SubmittedBefore: this.filters.createdOnEnd,
                ShowHiddenApplications: this.filters.ShowHiddenApplication,
                HasNoEmploymentRecord: this.filters.HasNoEmploymentRecord
            }
            return params;
        }

        createMailtoLink(application) {
            const emails = (application.OnboardingContacts || []).join(";");
            const email = "mailto:" + emails;
            const divisionName = (application.Division.Name || "").toUpperCase();
            const company = application.IcdOffice.IcdCorporationName + "-" || ""; // TODO.
            const owner = (application.IcdOffice.OwnerName || "").trim();
            const applicant = ((application.Applicant.FirstName || "") + " " + (application.Applicant.LastName || "")).trim();
            const subject = kendo.format("{0}: {1}{2}/{3}", divisionName, company, owner, applicant);
            const body = "First name: " + application.Applicant.FirstName + "\n"
                + "Last name: " + application.Applicant.LastName + "\n"
                + "Email address: " + application.Applicant.EmailAddress + "\n"
                + "Phone number: " + (application.Applicant.CellPhone?.PhoneNumber || "")
                + (application.Applicant.CellPhone && application.Applicant.CellPhone.Extension !== undefined ? " ext. " + application.Applicant.CellPhone.Extension : "")
                + "\n"
                + (application.Applicant.DateOfBirth ? "Date of Birth: " + moment(application.Applicant.DateOfBirth).format("YYYY-MM-DD") + "\n" : "")
                + "Owner: " + application.IcdOffice.OwnerName + "\n"
                + "Office: " + application.IcdOffice.OfficeName + "\n"
                + "\n\n"
                + "<Download and attach any supplementary files from Apex>";

            return email + "?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(body);
        }

        canAddToRoster(application) {
            if (!application.StatusHistory.length) {
                return false;
            }
            const currentStatus = application.StatusHistory[0].Status;
            // Check that the application refers to an employee that is ready to be added.
            const canBeAdded = (application.IsSelfSignUp && currentStatus.StatusType !== "SelfSignUp"
                && currentStatus.StatusType !== "Initial"
                || currentStatus.StatusType === "Approved")
                // If the employee ID is filled in, the employee's already on the roster.
                && !application.EmployeeID;
            // Show the button to either non-admin view users or to site super-users (developers/admins)
            const isPermitted = !this.isAdminView || this.roleAccess.IsSiteAdmin;
            return canBeAdded && isPermitted;
        }

        canEdit(application) {
            const currentStatus = application.StatusHistory[0].Status;
            // For the sake of permissions, this.isAdminView is used to show the internal view for normal internal users,
            // while the roleAccess is used to work around these restrictions for the sake of administration and development.
            return this.roleAccess.CanUpdate &&
                (this.roleAccess.IsSiteAdmin
                    || this.isAdminView
                    || currentStatus.StatusType === 'Incomplete');
        }

        dismissToast() {
            this.addedEmployee = null;
        }

        onAssignToExistingEmployee(application) {
            const employeesLoaded = this.employeeService.retrieveEmployees({ icdOfficeID: application.IcdOffice.Id });
            this.utilityService.showLoadingUntilResolved(employeesLoaded);

            this.$uibModal.open({
                ...AssignOnboardingEmployeeModal,
                resolve: {
                    onboardingApplication: () => {
                        return application;
                    },
                    employees: () => {
                        return employeesLoaded;
                    },
                    onAssign: () => {
                        return employeeID => {
                            const employeeAssigned = this.onboardingApplicationService
                                .patchOnboardingApplication(application.OnboardingApplicationID, {
                                    OnboardingApplicationID: application.OnboardingApplicationID,
                                    EmployeeID: employeeID
                                });
                            return this.utilityService.showLoadingUntilResolved(
                                this.utilityService.alertOnError(employeeAssigned, "Assign failed"));
                        }
                    }
                }
            }).result.then(employeeID => {
                // Employee ID has been successfully assigned.
                application.EmployeeID = employeeID;
            });
        }

        getOnboardingApplicationEmployee(application) {
            if (application.IsSelfSignUp && application.SelfSignUpEmployeeID) {

                return this.onboardingRosterService.retrieveOnboardingApplicationEmployee(application);
            } else {
                return this.onboardingRosterService.createEmployeeFromOnboardingApplication(application);
            }
        }
        /**
         * Start the process of adding the application's applicant to the owner's roster.
         */
        onAddToRoster(application) {
            this.getOnboardingApplicationEmployee(application)
                .then(e => this.displayEditEmployeeModal(e))
                .catch(ex => {
                    if (ex === 'cancel' || ex === 'escape key press') {
                        throw ex;
                    }
                    console.error(ex);
                    alert("Saving failed: " + this.utilityService.getExceptionMessage(ex));
                    throw ex;
                })
                .then(employee => {
                    // Modal saved the employee.
                    this.addedEmployee = employee;

                    return this.saveEmployeeProfileChanges(application, employee);
                })
                .then(() => {
                    //If employee is created redirect it to profile edit pagefor the sake of completing the profile
                    if (application.EmployeeID) {
                        window.location.href = "../User/UserProfile/" + application.EmployeeID;
                    }
                })
                .finally(() => {
                    this.utilityService.hideLoading();
                });

        }

        private saveEmployeeProfileChanges(application: OnboardingApplicationResponse, employee: EmployeeNameRM) {
            application.EmployeeID = employee.EmployeeID;

            // The cell phone needs to be patched in a separate request, since the employee profile is on a
            // different form.
            const patchRequest: Partial<EmployeeProfilePatchRequest> = {
                MobilePhone: application.Applicant.CellPhone,
                DateOfBirth: application.Applicant.DateOfBirth
            };
            const saving = this.employeeService.patchEmployeeProfile(employee.EmployeeID, patchRequest);

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(saving,
                    "Warning! Employee was created successfully but saving cell number failed"));

            return saving;
        }

        onChangeFilters() {
            if (this.loaded) {
                this.persistFilters();
                this.filters.PageNumber = this.pageNumber;
                this.filters.PageSize = this.pageSize;
                this.utilityService.showLoadingUntilResolved(
                    this.utilityService.alertOnError(this.loadOnboardingApplications(), "Changing page failed"));
            }

        }

        onChangePage() {
            if (this.loaded) {
                this.utilityService.showLoadingUntilResolved(
                    this.utilityService.alertOnError(this.loadOnboardingApplications(), "Changing page failed"));
                this.persistFilters();
            }
        }

        onEditOnboardingStatus(application) {
            // Default the status change to the application's current status.
            const statusChange = {
                // Default to the application's current status.
                NewStatusID: application.StatusHistory[0].Status.OnboardingApplicationStatusID,
                // Create a copy of the reference field values. If the reference field is required
                // at the newly selected status, it will be displayed. If the reference field is not required
                // at any status, it will always be visible.
                ReferenceFields: application.ReferenceFields.map(reference => {
                    return {
                        Name: reference.Name,
                        Value: reference.Value,
                        IsSensitive: reference.IsSensitive,
                        RequiredAtStatusID: reference.RequiredAtStatusID,
                        InputMask: reference.InputMask
                    };

                })
            };

            const availableStatuses = this.onboardingStatusService.retrieveAvailableStatuses(
                application.OnboardingApplicationID);

            this.$uibModal
                .open({
                    ...EditOnboardingApplicationStatusModal,
                    resolve: {
                        onboardingApplication: () => application,
                        statusChange: () => statusChange,
                        statuses: () => availableStatuses,
                        isAdminView: () => this.isAdminView || this.roleAccess.IsSiteAdmin
                    }
                }).result.then(application => {
                    // Reload applications data.
                    const param = this.generateQueryParameter();
                    return this.onboardingApplicationService.retrieveOnboardingApplicationsPage(param.PageIndex, param)
                }).then(page => {
                    this.onboardingApplications = page;
                    return page.Entities;
                });

            // Show loading spinner until the status data finishes loading.
            this.utilityService.showLoading();
            availableStatuses.catch(angular.noop).then(() => this.utilityService.hideLoading());
        }

        onEditSgiIdAssignment(application) {

            const sgiAssignmentHistories = this.onboardingApplicationService.retrieveEmployeeSgiIdHistory(
                application.IcdOffice.Id, application.EmployeeID)
            this.$uibModal.open({
                ...EditSgiIdAssignmentModal,
                resolve: {
                    onboardingApplication: () => application,
                    sgiAssignmentHistories: () => sgiAssignmentHistories,
                    isAdminView: () => this.isAdminView || this.roleAccess.IsSiteAdmin
                }
            });
            this.utilityService.showLoading();
            sgiAssignmentHistories.catch(angular.noop).then(() => this.utilityService.hideLoading());
        }

        onTransferOnboardingApplication(application) {

            const sgiAssignmentHistories = this.onboardingApplicationService.retrieveEmployeeSgiIdHistory(
                application.IcdOffice.Id, application.EmployeeID)
            const employee = this.employeeService.retrieveNewEmployeeModel();
            this.$q.all([sgiAssignmentHistories, employee])
                .then(v => {
                    this.$uibModal.open({
                        ...TransferOnboardingApplicationModal,
                        resolve: {
                            onboardingApplication: () => application,
                            sgiAssignmentHistories: () => v[0],
                            isAdminView: () => this.isAdminView || this.roleAccess.IsSiteAdmin,
                            employee: () => v[1]
                        }
                    }).result.then(application => {
                        // Reload applications data.
                        const param = this.generateQueryParameter();
                        return this.onboardingApplicationService
                            .retrieveOnboardingApplicationsPage(param.PageIndex, param);
                    }).then(applications => {
                        this.onboardingApplications = applications;
                        return this.onboardingApplications;
                    });

                })
                .catch(ex => {
                    console.error(ex);
                    alert("Initial load failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    this.utilityService.hideLoading();
                });
        }

        onEdit(application) {
            // If no application was passed in, propagate the null value
            // to the edit form, which creates the initial model.
            const applicationVM = !application ? null : {
                OnboardingApplicationID: application.OnboardingApplicationID,
                DivisionID: application.Division.Id,
                IcdOfficeID: application.IcdOffice.Id,
                EmployeeID: application.EmployeeID,
                Applicant: {
                    FirstName: application.Applicant.FirstName,
                    LastName: application.Applicant.LastName,
                    EmailAddress: application.Applicant.EmailAddress,
                    CellPhone: {
                        PhoneNumber: application.Applicant.CellPhone.PhoneNumber,
                        Extension: application.Applicant.CellPhone.Extension
                    },
                    DateOfBirth: application.Applicant.DateOfBirth
                },
                ReferenceFields: application.ReferenceFields,
                StatusHistory: application.StatusHistory
            };
            this.$uibModal.open({
                ...EditOnboardingApplicationModal,
                resolve: {
                    application: () => applicationVM,
                    showPasswordEditor: this.showOnboardingPasswordEditor,
                    isSiteAdmin: this.isAdminView || this.roleAccess.IsSiteAdmin
                },
            }).result.then(application => {
                // Reload applications data.
                const param = this.generateQueryParameter();
                return this.onboardingApplicationService.retrieveOnboardingApplicationsPage(param.PageIndex, param);
            }).then(applications => {
                this.onboardingApplications = applications;
                return this.onboardingApplications;
            });
        }

        onExport() {
            const params = this.generateQueryParameter();
            params.PageIndex = null;
            params.PageSize = null;
            const queryString = this.utilityService.queryStringify(params);
            window.location.href = this.exportXlsxUrl + queryString;
        }

        onReuploadDocument(application, document, title) {
            const documentGroup = application.OnboardingDocumentGroups.filter(group => {
                return group.OnboardingPolicyDocumentGroupID === document.OnboardingPolicyDocumentGroupID;
            })[0];
            this.$uibModal.open({
                component: "apexEditOnboardingDocumentModal",
                resolve: {
                    onboardingDocument: () => document,
                    onboardingDocumentGroup: () => documentGroup,
                    documentTitle: () => title,
                    onboardingApplicationID: () => application.OnboardingApplicationID
                },
                windowClass: 'xx-dialog pagespinner',
                size: 'lg',
                backdrop: 'static'
            });
        }

        onChangeBooleanReferenceField(onboardingApplication, referenceField) {
            const index = onboardingApplication.ReferenceFields.reduce((index, field, i) => {
                if (index != null) {
                    return index;
                }
                if (field.Name === referenceField.Name && field.ValueType === "Boolean") {
                    return i;
                }
                return null;
            }, null);

            if (index === null) {
                console.error("Boolean reference field not found by name: " + referenceField.Name)
                alert("Changing status failed: not found");
                return;
            }
            const updated = this.onboardingApplicationService.updateReferenceFieldValue(
                onboardingApplication.OnboardingApplicationID, index, referenceField.Value);

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(updated, "Changing status failed"));
        }

        viewOnboardingApplicationDetail(application) {
            this.$uibModal.open({
                ...OnboardingApplicationDetailModal,
                resolve: {
                    onboardingApplication: () => {
                        return application;
                    }
                }
            });
        }

        onDelete(application) {
            if (!confirm("This application, including any uploaded documents, will be deleted.")) {
                return;
            }

            this.utilityService.showLoading();
            this.onboardingApplicationService.deleteOnboardingApplication(application.OnboardingApplicationID)
                .then(() => {
                    application.deleted = true;
                })
                .catch(ex => {
                    console.error(ex);
                    alert("Delete failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    this.utilityService.hideLoading();
                });
        }

        onSetSort(field) {

            if (field === this.sortColumn.field) {
                this.sortColumn.dir *= -1;
            }
            this.sortColumn.field = field;
            this.filters.SortFields = [(this.sortColumn.dir === -1 ? "-" : "") + field];
            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(this.loadOnboardingApplications(), "Sorting failed"));

        }

        /**
         * Display a modal to add the employee to the owner's roster.
         * @employee Employee The employee to be added to the roster.
         */
        displayEditEmployeeModal(employee) {
            return this.$uibModal.open({
                ...AddApplicantToRosterModal,
                resolve: {
                    employee: employee
                }
            }).result;
        }

        persistFilters() {
            // Don't persist dates, since it is not immediately obvious when they have been persisted.
            localStorage.setItem("onboardingApplicationsListFilters", JSON.stringify({
                FirstName: this.filters.FirstName,
                LastName: this.filters.LastName,
                StatusName: this.filters.StatusName,
                OwnerName: this.filters.OwnerName,
                SubmittedBy: this.filters.SubmittedBy
            }));
        }

        loadFilters() {
            const filtersJson = localStorage.getItem("onboardingApplicationsListFilters");
            if (filtersJson) {
                const filtersObj = JSON.parse(filtersJson);
                filtersObj.PageNumber = this.pageNumber;
                filtersObj.PageSize = this.pageSize;
                return filtersObj;
            }
            // Initialize the filters.
            this.sortColumn.field = "SubmittedOn"
            this.sortColumn.dir = -1;
            const filters = {
                PageNumber: this.pageNumber,
                PageSize: this.pageSize,
                StatusID: this.onboardingStatuses
                    .filter(status => status.StatusType === "Initial")
                    .map(status => status.OnboardingApplicationStatusID)[0],
                SortFields: [
                    "-SubmittedOn"
                ]
            };
            return filters;
        }

    }
}

